<template>
  <div
    class="in-viewer-picture dialog-viewer-video" 
    :class="{'full': isPlayFull}"
  >
    <div
      class="viewer-container"
      :class="{ 'full': isPlayFull, 'hidden': isHidden }"
    >
      <div
        class="viewer-container__inner" 
        :style="{ 'background-image': isPlayStart ? 'none' : `url(${defaultCover})` }"
      >
        <div
          class="player" ref="player"
          v-show="!!resource.id"
        />
        <div class="play-fail" v-if="isPlayFail">
          <section>
            <span class="xlpfont xlp-icon-tip" />
            <span>网页无法支持该视频格式，请下载客户端播放</span>
          </section>
        </div>
        <div class="speed-tip" v-show="showSpeedTip">
          当前切换到<em>{{ curSpeed }}倍</em>速度播放
        </div>
        <div
          class="xlp-play-loading"
          v-show="((!isPlay || !isPlayStart) && !isPlayFail && !isPause && !isPlayend) || isSeeking || autoFail"
        >
          <span class="xlp-play-loading__icon" :style="{ 'visibility': autoFail ? 'hidden' : 'visible' }" />
          <!-- eslint-disable-next-line vue/no-v-html -->
          <span class="xlp-play-loading__text" v-html="loadingTxt" />
        </div>
        <div
          class="xlp-player-bar" v-if="showControl"
          :class="{ 'hidden': isHidden }"
        >
          <div class="xlp-player-bar__progress">
            <td-slider
              class="in-viewer-picture"
              v-model.value="videoProgress" :value="videoProgress"
              :scales="[0, 100]" @change="setVideoSeek"
              @release="startVideoSeek" @update:value="videoProgress = $event"
            />
          </div>
          <div class="xlp-player-bar__control">
            <div class="xlp-player-bar__action">
              <a
                href="javascript:;" 
                class="xlp-player-bar__play"
                @click="statClickPlay"
              >
                <i class="xlpfont" :class="`xlp-icon-${isPlay ? 'pause' : 'play'}`" />
              </a>
              <div class="xlp-player-bar__time">
                {{ videoCurTime }}
                <span class="xlp-line">/</span>
                {{ videoDuration }}
              </div>
            </div>
            <div class="xlp-player-bar__operate">
              <div class="xlp-player-bar__speed">
                <span>倍速</span>
                <ul class="xlp-player-bar__speed-ul">
                  <li
                    v-for="speed in speedList"
                    :class="{ 'vip': speed.type === 'vip', 'on': Number(curSpeed) === speed.value }"
                    :key="String(speed.value)" @click="setSpeed(speed)"
                  >
                    {{ speed.name }}
                  </li>
                </ul>
              </div>
              <div
                class="xlp-player-bar__volume" @mousewheel="wheelVolume"
                :title="videoVolume"
              >
                <i
                  class="xlpfont" :class="`xlp-btn-mute-${isMuted ? 'on' : 'off'}`"
                  @click.stop="isMuted = !isMuted"
                />
                <td-slider
                  class="in-viewer-picture"
                  vertical v-model.value="videoVolume"
                  :scales="[0, 100]" :value="videoVolume"
                />
              </div>
              <a
                href="javascript:;" class="xlp-player-bar__button"
                @click="isPlayFull = !isPlayFull"
              >
                <i class="xlpfont" :class="`xlp-btn-full-${isPlayFull ? 'back' : 'play'}`" />
              </a>
            </div>
          </div>
        </div>
        <span
          class="btn-play" v-show="isPause && !isPlayend"
          @click="isPlay = true, isPause = !isPause"
        />
        <p
          class="play-btns" v-if="isPlayend && !isPlayFail && !autoPlayNext"
          @click="replay"
        >
          <span>
            <i class="xlpfont xlp-btn-replay" />
            <i>重播</i>
          </span>
        </p>
      </div>
    </div>
  </div>
</template>

<script >
import { logInfo, reportPlayVideo } from '@remote/utils/logger'
import debounce from 'lodash/debounce'
import { PACKAGE_NAME } from '@remote/api/config'
import { store } from '@remote/store'
import '@remote/assets/scss/player.css'
import TDSlider from './Slider.vue'
import { getHlsConfig } from '../file-processor/helper/hls'
import { secondToDate, fullscreen } from '../file-processor/helper/utils/util'
import { TheViewerVideoProps, createTheViewerVideoInitData } from './TheViewerVideo'

let player = null
let timer = null

export default {
  components: {
    'td-slider': TDSlider,
  },
  props: TheViewerVideoProps,
  emits: ['close', 'playend'],
  data () {
    return createTheViewerVideoInitData()
  },
  computed: {
    resource () {
      return this.previewFileInfo || {}
    },
    medias () {
      return this.resource.medias || []
    },
    isVip () {
      return store?.getters?.isVip
    },
    curUser () {
      return store?.state?.user
    },
    loadingTxt () {
      if (this.autoFail) return '无法自动播放，可前往设置：<br>Safari设置-此网站设置-自动播放-选择“允许全部自动播放”'
      return !this.isPlayerInited ? '播放器初始化中 …' : (this.isPlayerParsing ? '播放器视频解析中 …' : `视频文件加载中  (${this.speedTxt}KB/s) …`)
    },
    isInSafebox () {
      return this.$store?.state?.safebox?.isIn
    },
  },
  watch: {
    isPlay (val) {
      if (!player || this.isPlayFail) return
      if (val) {
        this.isPause = false
        this.play()
      } else {
        this.pause()
      }
    },
    isMuted (val) {
      if (!player) return
      if (this.isPlayFail) return
      if (val) {
        player.volume = 0
        this.videoVolumeCp = this.videoVolume
        this.videoVolume = 0
      } else {
        this.videoVolume = this.videoVolumeCp
        player.volume = this.videoVolume / 100
      }
    },
    videoVolume (val) {
      if (!player || this.isPlayFail) return
      player.volume = this.videoVolume / 100
    },
    isPlayFull (val) {
      if (!player) return
      if (!this.isBrowserVideo) player.pause()
      if (val) {
        fullscreen()
      } else {
        if (document.fullscreenElement) document.exitFullscreen()
        else if (document.webkitFullscreenElement) document.webkitCancelFullScreen()
      }
      setTimeout(() => this.resize(), 500)
    },
    resource (val) {
      if (val?.id) {
        this.createdPlayer()
      } else {
        console.log('destroyPlayer')
        this.destroyPlayer()
        this.initPlayparams()
        this.isPlay = false
        this.isPause = true
      }
    },
  },
  
  mounted () {
    document.addEventListener('fullscreenchange', this.handleFullscreenchange)
    document.addEventListener('mousemove', this.handleMousemove)
    this.videoProgress = this.defaultVideoProgress || 0
    this.videoCurTime = this.defaultVideoCurTime || '00:00:00'
  },
  unmounted () {
    this.videoCurTime = '00:00:00'
    document.removeEventListener('fullscreenchange', this.handleFullscreenchange)
    document.removeEventListener('mousemove', this.handleMousemove)
    this.destroyPlayer()
  },
  methods: {
    async createdPlayer () {
      console.log('createdPlayer', this.previewFileInfo)
      // this.videoCurTime = '00:00:00'

      if (player) {
        this.handleRemoveVideoEvent(player)
        player.pause()
        if (player.removeAttribute) player.removeAttribute('src')
        if (player.load) player.load()
        player.remove()
        player = null
      }

      this.initPlayparams()
      setTimeout(() => {
        const usefulMediasArray = this.medias?.filter(v => (v?.link?.url)) || []
        this.usefulMedia = usefulMediasArray[usefulMediasArray.length - 1]  // 选择最低的画质
        const isM3u8Play = this.medias?.some(item => ['mpegts'].includes(item.video.video_type))
        if (isM3u8Play) {
          const url = `//${PACKAGE_NAME}/ts_downloader?client_id=${'YvRh8xuh1qTMewHv'}&url=${encodeURIComponent(this.usefulMedia?.link?.url || this.previewFileInfo?.web_content_link)}`
          this.isBrowserVideo = true
          this.isDPlayerVideo = true
          this.initDplayer(url, this.usefulMedia?.link?.url || this.previewFileInfo?.web_content_link)
          if (this.previewFileInfo) logInfo('video_play_params', {
            path: this.previewFileInfo.file_path,
            palyUrl: this.usefulMedia?.link?.url || this.previewFileInfo?.web_content_link,
            size: this.previewFileInfo.size,
            playFormat: 'm3u8',
          })
        } else {
          this.isBrowserVideo = true
          this.isDPlayerVideo = false
          this.initBrowserVideo(decodeURIComponent(decodeURIComponent(this.usefulMedia?.link?.url || this.previewFileInfo?.web_content_link)), this.resource.mime_type)
          if (this.previewFileInfo) logInfo('video_play_params', {
            path: this.previewFileInfo.file_path,
            palyUrl: this.usefulMedia?.link?.url || this.previewFileInfo?.web_content_link,
            size: this.previewFileInfo.size,
            playFormat: 'mp4',
          })
        }
      }, 1000)

      if (this.autoPlay) timer = setTimeout(() => {
        this.showControl = false
        this.isPlayFail = true
        this.isPlayStart = false
        if (this.previewFileInfo) logInfo('video_play_error', {
          path: this.previewFileInfo.file_path,
          palyUrl: this.usefulMedia?.link?.url || this.previewFileInfo?.web_content_link, 
          size: this.previewFileInfo.size,
          playFormat: 'play_timeout',
        })
      }, 30 * 1000)
    },
    destroyPlayer () {
      if (player) {
        if (this.previewFileInfo?.id) reportPlayVideo(this.previewFileInfo.id, player?.duration * this.videoProgress / 99)
        this.handleRemoveVideoEvent(player)
        if (player.pause) {
          const ret = player.pause()
          if (ret instanceof Promise) ret.catch()
        }
        if (player.removeAttribute) player.removeAttribute('src')
        if (player.load) {
          const ret = player.load()
          if (ret instanceof Promise) ret.catch()
        }
        if (player.remove) player.remove()
        if (player.destroy) player.destroy()
        player = null
      }
      timer && clearInterval(timer)
    },
    initPlayparams () {
      this.showControl = true
      this.isPlayFull = false
      this.isPlay = !!this.autoPlay
      this.isPause = this.autoPlay ? false : true
      this.videoProgress = this.defaultVideoProgress || 0
      this.videoCurTime = this.defaultVideoCurTime || '00:00:00'
      this.isPlayFail = false
      this.isMuted = false
      this.isPlayend = false
      this.videoVolume = 100
      this.speedTxt = ''
      this.usefulMedia = {} // 若无m3u8,则会是原画质
      this.cacheBuffer = { left: 0, width: 0 }
      this.isPlayerInited = this.autoPlay ? false : true
      this.isPlayerParsing = false
      this.isBrowserVideo = false
      this.isDPlayerVideo = false
      this.isHidden = false
      this.hiddenStart = 0
      this.isPlayStart = false
    },
    resize () {
      if (this.isBrowserVideo) return
      const el = this.$refs.player
      const d = el.offsetWidth / el.offsetHeight
      if (this.videoScale > d) {
        player.canvas.style.width = '100%'
        player.canvas.style.height = 'auto'
      } else {
        player.canvas.style.width = 'auto'
        player.canvas.style.height = '100%'
      }
      player.play()
    },
    statClickPlay () {
      if (this.isPlayFail) return
      if (this.isPlayend) this.replay()
      this.isPlay = !this.isPlay
      this.isPause = !this.isPause
    },
    play () {
      if (this.isPlayFail) return
      this.autoFail = false
      player.play()
    },
    pause () {
      if (this.isPlayFail) return
      player.pause()
    },
    onShowSpeedTip () {
      this.showSpeedTip = true
      setTimeout(() => {
        this.showSpeedTip = false
      }, 2000)
    },
    statSpeed (val) {
      const speedVal = Number(val.toFixed(1)) === val ? val.toFixed(1) : val
      if (val > 1 && !this.isVip) return
      this.curSpeed = speedVal
      this.onShowSpeedTip()
    },
    setSpeed (info) {
      if (this.isPlayFail) return
      if (info.value > 1 && !this.isVip) {
        return window.open('https://pay.xunlei.com/pay.html?referfrom=v_pc_xlx_hytq_yp_storage&aidfrom=operation_bar')
      }
      this.statSpeed(info.value)
      player.playbackRate = info.value
    },
    setVideoSeek () {
      if (this.isPlayFail) return
      if (!player) return
      this.isSeek = true
      const currentTime = player.duration * this.videoProgress / 99
      this.videoCurTime = player && player.duration ? secondToDate(parseInt(currentTime)) : '00:00:00'
      if (this.previewFileInfo) reportPlayVideo(this.previewFileInfo.id, currentTime)
    },
    startVideoSeekImmediately (progress = 0) {
      if (this.isPlayFail || !player) return
      const p = progress || this.videoProgress
      const currentTime = player.duration * p / 99
      if (this.isBrowserVideo) {
        player.currentTime = parseInt(currentTime)
        this.isSeek = false
        if (this.isDPlayerVideo) {
          this.isSeeking = true
          // this.isPlay = false
        }
      } else {
        this.isSeeking = true
        this.isPlay = false
        this.isPlayerParsing = false
        if (player.seek) player.seek(currentTime)
      }
      this.videoCurTime = player && player.duration ? secondToDate(parseInt(currentTime)) : '00:00:00'

      if (this.isPlayend) {
        this.isPlayend = false
        this.isPlay = true
      }
      if (this.previewFileInfo) reportPlayVideo(this.previewFileInfo.id, currentTime)
    },
    startVideoSeek: debounce(function () {
      this.startVideoSeekImmediately()
    }, 1000, {
      leading: false,
      trailing: true,
    }),
    wheelVolume (e) {
      if (this.isPlayFail) return
      let currentVolume = this.videoVolume
      if (e.wheelDelta > 0) {
        currentVolume += 5
      } else {
        currentVolume -= 5
      }
      currentVolume = currentVolume < 0 ? 0 : (currentVolume > 100 ? 100 : currentVolume)
      this.videoVolume = currentVolume
      this.isMuted = currentVolume === 0
    },
    replay () {
      this.isPlayend = false
      if (this.isBrowserVideo) {
        player.currentTime = 0
        player.play()
      } else {
        player.seek(0)
      }
    },
    handleOnVideoEvent (player) {
      if (this.autoPlay) {
        let isProgressRecover = false
        player.oncanplay = () => {
          timer && clearInterval(timer)
          this.isPlayerInited = true
          this.isPlay = true
          this.isPlayStart = true
          if (!isProgressRecover) {
            isProgressRecover = true
            const viewedPoint = +this.previewFileInfo?.play_seconds || 0
            const duration = player.duration || 0
            const progress = this.defaultVideoProgress || Math.floor(100 * viewedPoint / duration)
            console.log('[视频播放][追踪播放进度]:', viewedPoint, duration, progress)
            // 追踪播放进度
            this.videoProgress = progress > 99 ? 99 : 0
            this.startVideoSeekImmediately(progress)
          }
        }
      }

      player.onclick = (e) => {
        e.stopPropagation()
        this.statClickPlay()
      }

      player.onerror = (err) => {
        this.showControl = false
        this.isPlayFail = true
        if (this.previewFileInfo) logInfo('video_play_error', {
          path: this.previewFileInfo.file_path,
          palyUrl: this.usefulMedia?.link?.url || this.previewFileInfo?.web_content_link,
          size: this.previewFileInfo.size,
          playFormat: err || 'onerror',
        })
      }

      player.onpause = () => {
        if (!this.isPictureinpicture) return
        console.log('onpause')
        this.isPlay = false
        this.isPause = true
      }

      player.onplay = () => {
        if (!this.isPictureinpicture) return
        this.isPlay = true
        this.isPause = false
      }

      // https://zhuanlan.zhihu.com/p/38251413
      player.addEventListener('enterpictureinpicture', () => {
        this.isPictureinpicture = true
      })

      player.addEventListener('leavepictureinpicture', () => {
        this.isPictureinpicture = false
        if (player.paused) {
          this.isPlay = false
          this.isPause = true
        }
      })

      player.ontimeupdate = () => {
        this.isSeeking = false
        this.isPlayFail = false
        this.showControl = true

        if (this.isSeek) return
        this.videoCurTime = secondToDate(parseInt(player.currentTime))
        if (player.duration * 1000 < this.tryDuration) this.tryDuration = player.duration * 1000
        this.videoProgress = 1 + Math.floor((player.currentTime / player.duration) * 98)

        if (player.currentTime >= player.duration) {
          this.isPlayend = true
          this.isPause = true
          this.isPlay = false
          this.$emit('playend', this.id)
        }

        if (!this.isHidden) {
          const currentTime = player.currentTime
          if (this.hiddenStart) {
            if (Math.abs(currentTime - this.hiddenStart) >= 5) {
              this.isHidden = true
              this.hiddenStart = 0
            }
          } else {
            this.hiddenStart = currentTime
          }
        }
      }
    },
    handleRemoveVideoEvent (player) {
      player.oncanplay = null
      player.onclick = null
      player.onerror = null
      player.onpause = null
      player.onplay = null
      player.ontimeupdate = null
      player.onwaiting = null
      player.onprogress = null
      // player.removeEventListener('enterpictureinpicture')
      // player.removeEventListener('leavepictureinpicture')
    },
    initBrowserVideo (url, mineType) {
      let isInit = true
      player = document.createElement('video')
      const source = document.createElement('source')
      source.type = mineType
      source.src = url
      player.controls = false
      player.autoplay = !!this.autoPlay
      if (!this.autoPlay) player.poster = this.defaultCover
      player.appendChild(source)
      const el = this.$refs.player
      if(!el) return
      el.innerHTML = ''
      el.appendChild(player)
      this.handleOnVideoEvent(player)

      player.onwaiting = () => {
        if (!isInit) {
          // this.isPlay = false
          this.isSeeking = true
        }
        isInit = false
      }

      player.onprogress = () => {
        if (!player) return
        if (player.buffered.end && player.buffered.end.start) {
          this.speedTxt = (player.buffered.end(0) - player.buffered.start(0)).toFixed()
        }
      }

      if(this.autoPlay) {
        player.play()
      } else {
        player.pause()
      }
    },
    initDplayer (url, baseUrl) {
      // 加载ts文件进度
      const onProgress = data => {
        this.speedTxt = data
      }

      const onError = (err) => {
        this.showControl = false
        this.isPlayFail = true
        if (this.previewFileInfo) logInfo('video_play_error', {
          path: this.previewFileInfo.file_path,
          palyUrl: this.usefulMedia?.link?.url || this.previewFileInfo?.web_content_link,
          size: this.previewFileInfo.size,
          playFormat: err || 'onerror',
        })
      }

      const hlsConfig = getHlsConfig({ m3u8Path: url, baseUrl, onProgress, onError })
      const customHls = (video) => {
        player = video
        const hls = new Hls(hlsConfig)
        hls.attachMedia(player)
        hls.on(Hls.Events.MEDIA_ATTACHED, () => {
          hls.loadSource(player.src)
        })
        this.handleOnVideoEvent(player)
      }

      new DPlayer({
        container: this.$refs.player,
        autoplay: !!this.autoPlay,
        video: {
          pic: this.autoPlay ? '' : this.defaultCover,
          preload: 'metadata',
          hotkey: true,
          type: 'customHls',
          customType: {
            customHls: customHls,
          },
        },
      })
    },
    handleFullscreenchange () {
      if (document.fullscreenElement) {
        this.isPlayFull = true
      } else {
        this.isPlayFull = false
      }
    },
    handleMousemove () {
      // console.log('handleMousemove', this.isHidden)
      this.isHidden = false
    },
  },
}
</script>

<style lang="scss">
.dialog-viewer-video.in-viewer-picture {
  width: 980PX;
  height: 551PX;
  bottom: 0;
  right: 0;
  margin: auto;
  position: fixed;
  top: 0;
  left: 0;
  z-index: 999999;
  background: rgba(0, 0, 0, 0.85);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  &.full {
    width: 100%;
    height: 100%;
  }
  .viewer-container {
    display: flex;
    width: 100%;
    max-width: 100vw;
    height: 100%;
    flex-direction: column;

    @media screen and (max-width: 760px) {
      width: 98%;
    }

    &.full {
      width: 100%;
      height: 100%;

      &.hidden {
        cursor: none;
      }

      header {
        display: none;
      }

      .viewer-container__inner {
        height: 100%
      }
    }

    &__inner {
      width: 100%;
      flex: 1;
      position: relative;
      background-color: #000;
      background-size: contain;
      background-repeat: no-repeat;
      background-position: center;
      overflow: hidden;

      .player {
        width: 100%;
        height: 100%;
        display: flex;
        justify-content: center;
        align-items: center;
      }

      video {
        width: 100%;
        height: 100%;
        outline: none;
      }

      .play-fail {
        position: absolute;
        top: 50%;
        margin: 0;
        width: 100%;
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;
        color: #fff;
        font-size: 13PX;

        .xlp-icon-tip {
          font-size: 28PX;
        }

        >section {
          display: flex;
          align-items: center;
          position: relative;
          span:nth-child(2) {
            margin-left: 30PX;
          }
        }

        &__btn {
          height: 36PX;
          line-height: 36PX;
          padding: 0 16PX 0 10PX;
          margin: 0 6PX;
          border-radius: 56PX;
          border: 1PX solid #fff;
          cursor: pointer;
          font-size: 12PX;

          &:before {
            font-size: 14PX;
          }

          &:hover {
            color: var(--color-primary);
            border-color: var(--color-primary);
          }
        }

        .qrcode-app-tip {
          display: none;
          left: 94PX;
          top: 44PX;
        }

        &__btn-app {
          &:hover {
            +.qrcode-app-tip {
              display: flex;
            }
          }
        }
      }

      .btn-play {
        position: absolute;
        background: url('../../assets/images/play.png');
        background-size: cover;
        width: 75PX;
        height: 75PX;
        top: 50%;
        left: 50%;
        margin-top: -75PX;
        margin-left: -37.5PX;
        cursor: pointer;
        z-index: 10;
      }

      .play-btns {
        position: absolute;
        top: 50%;
        left: 0;
        margin-top: -40px;
        display: flex;
        justify-content: center;
        width: 100%;
        z-index: 10;

        span {
          color: #fff;
          font-size: 13px;
          cursor: pointer;
          text-align: center;

          i {
            position: relative;
            display: block;
            margin: 0 47px;
            &:hover {
              background: none;
            }
          }

          i.xlpfont {
            font-size: 30px;
          }
        }
      }

      .speed-tip {
        position: absolute;
        bottom: 120PX;
        left: 60PX;
        background: #000;
        border-radius: 20PX;
        font-size: 13PX;
        padding: 10pX 14PX;
        color: #fff;
        z-index: 10;

        em {
          color: var(--color-primary);
        }
      }
      .xlp-play-loading__text {
        text-align: center;
        margin-top:12PX;
      }
      .xlp-player-bar__speed ul li.on {
        @include themeify {
          color: themed("download_progress_up");
        }
      }
      .xlp-player-bar__volume .td-slider .td-slider__button {
        margin: -4PX 0 -2PX -8PX;
      }
      .xlp-player-bar__play,
      .xlp-player-bar__button,
      .xlp-player-bar__volume {
        display: inline-block;
        height: 24PX;
        width: 24PX;
        .xlpfont {
          font-size: 16PX;
          &:hover {
            @include themeify {
              color: themed("download_progress_up");
            }
          }
        }
      }
    }
  }


  .dplayer-mask,
  .dplayer-danmaku,
  .dplayer-bezel,
  .dplayer-controller-mask,
  .dplayer-controller,
  .dplayer.dplayer-mobile .dplayer-mobile-play {
    display: none;
  }
}

.dialog-viewer-video.in-viewer-picture.in-cinema__detail.full {
  position: fixed !important;
}
.dialog-viewer-video.in-viewer-picture.in-cinema__detail .viewer-container__inner {
  background-size: cover;
}
</style>
